/**
 * @license
 * Copyright (c) 2021 Handsoncode. All rights reserved.
 */
import assert from 'assert';
import { AbsoluteCellRange } from '../AbsoluteCellRange';
import { absolutizeDependencies } from '../absolutizeDependencies';
import { CellError, ErrorType, simpleCellAddress } from '../Cell';
import { ErrorMessage } from '../error-message';
import { SimpleRangeValue } from '../interpreter/SimpleRangeValue';
import { collectDependencies, NamedExpressionDependency } from '../parser';
import { ColumnsSpan, RowsSpan } from '../Span';
import { StatType } from '../statistics';
import { EmptyCellVertex, FormulaCellVertex, MatrixVertex, RangeVertex, ValueCellVertex, } from './';
import { AddressMapping } from './AddressMapping/AddressMapping';
import { collectAddressesDependentToMatrix } from './collectAddressesDependentToMatrix';
import { Graph } from './Graph';
import { MatrixMapping } from './MatrixMapping';
import { RangeMapping } from './RangeMapping';
import { SheetMapping } from './SheetMapping';
import { EmptyValue, getRawValue } from '../interpreter/InterpreterValue';
export class DependencyGraph {
    constructor(addressMapping, rangeMapping, sheetMapping, matrixMapping, stats, lazilyTransformingAstService, functionRegistry, namedExpressions) {
        this.addressMapping = addressMapping;
        this.rangeMapping = rangeMapping;
        this.sheetMapping = sheetMapping;
        this.matrixMapping = matrixMapping;
        this.stats = stats;
        this.lazilyTransformingAstService = lazilyTransformingAstService;
        this.functionRegistry = functionRegistry;
        this.namedExpressions = namedExpressions;
        this.dependencyQueryAddresses = (vertex) => {
            if (vertex instanceof RangeVertex) {
                return this.rangeDependencyQuery(vertex).map(([address, _]) => address);
            }
            else {
                const dependenciesResult = this.formulaDependencyQuery(vertex);
                if (dependenciesResult !== undefined) {
                    const [address, dependencies] = dependenciesResult;
                    return dependencies.map((dependency) => {
                        if (dependency instanceof NamedExpressionDependency) {
                            const namedExpression = this.namedExpressions.namedExpressionOrPlaceholder(dependency.name, address.sheet);
                            return namedExpression.address;
                        }
                        else {
                            return dependency;
                        }
                    });
                }
                else {
                    return undefined;
                }
            }
        };
        this.dependencyQueryVertices = (vertex) => {
            if (vertex instanceof RangeVertex) {
                return this.rangeDependencyQuery(vertex).map(([_, v]) => v);
            }
            else {
                const dependenciesResult = this.formulaDependencyQuery(vertex);
                if (dependenciesResult !== undefined) {
                    const [address, dependencies] = dependenciesResult;
                    return dependencies.map((dependency) => {
                        if (dependency instanceof AbsoluteCellRange) {
                            return this.rangeMapping.fetchRange(dependency.start, dependency.end);
                        }
                        else if (dependency instanceof NamedExpressionDependency) {
                            const namedExpression = this.namedExpressions.namedExpressionOrPlaceholder(dependency.name, address.sheet);
                            return this.addressMapping.fetchCell(namedExpression.address);
                        }
                        else {
                            return this.addressMapping.fetchCell(dependency);
                        }
                    });
                }
                else {
                    return undefined;
                }
            }
        };
        this.rangeDependencyQuery = (vertex) => {
            const allDeps = [];
            const { smallerRangeVertex, restRange } = this.rangeMapping.findSmallerRange(vertex.range); //checking whether this range was splitted by bruteForce or not
            let range;
            if (smallerRangeVertex !== null && this.graph.adjacentNodes(smallerRangeVertex).has(vertex)) {
                range = restRange;
                allDeps.push([new AbsoluteCellRange(smallerRangeVertex.start, smallerRangeVertex.end), smallerRangeVertex]);
            }
            else { //did we ever need to use full range
                range = vertex.range;
            }
            for (const address of range.addresses(this)) {
                const cell = this.addressMapping.getCell(address);
                if (cell instanceof EmptyCellVertex) {
                    cell.address = address;
                }
                if (cell !== null) {
                    allDeps.push([address, cell]);
                }
            }
            return allDeps;
        };
        this.formulaDependencyQuery = (vertex) => {
            let formula;
            let address;
            if (vertex instanceof FormulaCellVertex) {
                address = vertex.getAddress(this.lazilyTransformingAstService);
                formula = vertex.getFormula(this.lazilyTransformingAstService);
            }
            else if (vertex instanceof MatrixVertex && vertex.isFormula()) {
                address = vertex.getAddress();
                formula = vertex.getFormula();
            }
            else {
                return undefined;
            }
            const deps = collectDependencies(formula, this.functionRegistry);
            return [address, absolutizeDependencies(deps, address)];
        };
        this.graph = new Graph(this.dependencyQueryVertices);
    }
    /*
     * Invariants:
     * - empty cell has associated EmptyCellVertex if and only if it is a dependency (possibly indirect, through range) to some formula
     */
    static buildEmpty(lazilyTransformingAstService, config, functionRegistry, namedExpressions, stats) {
        const addressMapping = new AddressMapping(config.chooseAddressMappingPolicy);
        const rangeMapping = new RangeMapping();
        return new DependencyGraph(addressMapping, rangeMapping, new SheetMapping(config.translationPackage), new MatrixMapping(), stats, lazilyTransformingAstService, functionRegistry, namedExpressions);
    }
    setFormulaToCell(address, ast, dependencies, hasVolatileFunction, hasStructuralChangeFunction) {
        const vertex = this.addressMapping.getCell(address);
        this.ensureThatVertexIsNonMatrixCellVertex(vertex);
        const newVertex = new FormulaCellVertex(ast, address, this.lazilyTransformingAstService.version());
        this.exchangeOrAddGraphNode(vertex, newVertex);
        this.addressMapping.setCell(address, newVertex);
        this.processCellDependencies(dependencies, newVertex);
        this.graph.markNodeAsSpecialRecentlyChanged(newVertex);
        if (hasVolatileFunction) {
            this.markAsVolatile(newVertex);
        }
        if (hasStructuralChangeFunction) {
            this.markAsDependentOnStructureChange(newVertex);
        }
        this.correctInfiniteRangesDependency(address);
    }
    setParsingErrorToCell(address, errorVertex) {
        const vertex = this.addressMapping.getCell(address);
        this.ensureThatVertexIsNonMatrixCellVertex(vertex);
        this.exchangeOrAddGraphNode(vertex, errorVertex);
        this.addressMapping.setCell(address, errorVertex);
        this.graph.markNodeAsSpecialRecentlyChanged(errorVertex);
        this.correctInfiniteRangesDependency(address);
    }
    setValueToCell(address, value) {
        const vertex = this.addressMapping.getCell(address);
        this.ensureThatVertexIsNonMatrixCellVertex(vertex);
        if (vertex instanceof ValueCellVertex) {
            const oldValue = vertex.getValues();
            if (oldValue.rawValue !== value.rawValue) {
                vertex.setValues(value);
                this.graph.markNodeAsSpecialRecentlyChanged(vertex);
            }
        }
        else {
            const newVertex = new ValueCellVertex(value.parsedValue, value.rawValue);
            this.exchangeOrAddGraphNode(vertex, newVertex);
            this.addressMapping.setCell(address, newVertex);
            this.graph.markNodeAsSpecialRecentlyChanged(newVertex);
        }
        this.correctInfiniteRangesDependency(address);
    }
    setCellEmpty(address) {
        const vertex = this.addressMapping.getCell(address);
        if (vertex === null) {
            return;
        }
        this.ensureThatVertexIsNonMatrixCellVertex(vertex);
        if (this.graph.adjacentNodes(vertex).size > 0) {
            const emptyVertex = new EmptyCellVertex(address);
            this.exchangeGraphNode(vertex, emptyVertex);
            if (this.graph.adjacentNodesCount(emptyVertex) === 0) {
                this.removeVertex(emptyVertex);
                this.addressMapping.removeCell(address);
            }
            else {
                this.graph.markNodeAsSpecialRecentlyChanged(emptyVertex);
                this.addressMapping.setCell(address, emptyVertex);
            }
        }
        else {
            this.removeVertex(vertex);
            this.addressMapping.removeCell(address);
        }
    }
    ensureThatVertexIsNonMatrixCellVertex(vertex) {
        assert.ok(!(vertex instanceof MatrixVertex), 'Illegal operation');
    }
    clearRecentlyChangedVertices() {
        this.graph.clearSpecialNodesRecentlyChanged();
    }
    verticesToRecompute() {
        return new Set([...this.graph.specialNodesRecentlyChanged, ...this.volatileVertices()]);
    }
    processCellDependencies(cellDependencies, endVertex) {
        cellDependencies.forEach((dep) => {
            if (dep instanceof AbsoluteCellRange) {
                const range = dep;
                let rangeVertex = this.getRange(range.start, range.end);
                if (rangeVertex === undefined) {
                    rangeVertex = new RangeVertex(range);
                    this.rangeMapping.setRange(rangeVertex);
                }
                this.graph.addNode(rangeVertex);
                if (!range.isFinite()) {
                    this.graph.markNodeAsInfiniteRange(rangeVertex);
                }
                const { smallerRangeVertex, restRange } = this.rangeMapping.findSmallerRange(range);
                if (smallerRangeVertex) {
                    this.graph.addEdge(smallerRangeVertex, rangeVertex);
                    if (rangeVertex.bruteForce) {
                        rangeVertex.bruteForce = false;
                        for (const cellFromRange of range.addresses(this)) { //if we ever switch heuristic to processing by sorted sizes, this would be unnecessary
                            this.graph.removeEdge(this.fetchCell(cellFromRange), rangeVertex);
                        }
                    }
                }
                else {
                    rangeVertex.bruteForce = true;
                }
                const matrix = this.matrixMapping.getMatrix(restRange);
                if (matrix !== undefined) {
                    this.graph.addEdge(matrix, rangeVertex);
                }
                else {
                    for (const cellFromRange of restRange.addresses(this)) {
                        this.graph.addEdge(this.fetchCellOrCreateEmpty(cellFromRange), rangeVertex);
                    }
                }
                this.graph.addEdge(rangeVertex, endVertex);
                if (range.isFinite()) {
                    this.correctInfiniteRangesDependenciesByRangeVertex(rangeVertex);
                }
            }
            else if (dep instanceof NamedExpressionDependency) {
                const sheetOfVertex = endVertex.getAddress(this.lazilyTransformingAstService).sheet;
                const namedExpressionVertex = this.fetchNamedExpressionVertex(dep.name, sheetOfVertex);
                this.graph.addEdge(namedExpressionVertex, endVertex);
            }
            else {
                this.graph.addEdge(this.fetchCellOrCreateEmpty(dep), endVertex);
            }
        });
    }
    fetchNamedExpressionVertex(expressionName, sheetId) {
        const namedExpression = this.namedExpressions.namedExpressionOrPlaceholder(expressionName, sheetId);
        return this.fetchCellOrCreateEmpty(namedExpression.address);
    }
    exchangeNode(addressFrom, addressTo) {
        const vertexFrom = this.fetchCellOrCreateEmpty(addressFrom);
        const vertexTo = this.fetchCellOrCreateEmpty(addressTo);
        this.addressMapping.removeCell(addressFrom);
        this.exchangeGraphNode(vertexFrom, vertexTo);
    }
    correctInfiniteRangesDependenciesByRangeVertex(vertex) {
        for (const range of this.graph.infiniteRanges) {
            const infiniteRangeVertex = range;
            const intersection = vertex.range.intersectionWith(infiniteRangeVertex.range);
            if (intersection === null) {
                continue;
            }
            for (const address of intersection.addresses(this)) {
                this.graph.addEdge(this.fetchCellOrCreateEmpty(address), range);
            }
        }
    }
    correctInfiniteRangesDependency(address) {
        let vertex = null;
        for (const range of this.graph.infiniteRanges) {
            const infiniteRangeVertex = range;
            if (infiniteRangeVertex.range.addressInRange(address)) {
                vertex = vertex || this.fetchCellOrCreateEmpty(address);
                this.graph.addEdge(vertex, infiniteRangeVertex);
            }
        }
    }
    fetchCellOrCreateEmpty(address) {
        let vertex = this.addressMapping.getCell(address);
        if (!vertex) {
            vertex = new EmptyCellVertex(address);
            this.graph.addNode(vertex);
            this.addressMapping.setCell(address, vertex);
        }
        return vertex;
    }
    removeRows(removedRows) {
        if (this.matrixMapping.isFormulaMatrixInRows(removedRows)) {
            throw Error('It is not possible to remove row with matrix');
        }
        this.stats.measure(StatType.ADJUSTING_GRAPH, () => {
            for (const vertex of this.addressMapping.verticesFromRowsSpan(removedRows)) {
                for (const adjacentNode of this.graph.adjacentNodes(vertex)) {
                    this.graph.markNodeAsSpecialRecentlyChanged(adjacentNode);
                }
                if (vertex instanceof MatrixVertex) {
                    continue;
                }
                this.removeVertex(vertex);
            }
        });
        this.stats.measure(StatType.ADJUSTING_MATRIX_MAPPING, () => {
            this.truncateMatricesAfterRemovingRows(removedRows);
        });
        this.stats.measure(StatType.ADJUSTING_ADDRESS_MAPPING, () => {
            this.addressMapping.removeRows(removedRows);
        });
        this.stats.measure(StatType.ADJUSTING_RANGES, () => {
            this.truncateRanges(removedRows, address => address.row);
        });
        this.addStructuralNodesToChangeSet();
    }
    removeSheet(removedSheetId) {
        const matrices = new Set();
        for (const [adr, vertex] of this.addressMapping.sheetEntries(removedSheetId)) {
            if (vertex instanceof MatrixVertex) {
                if (matrices.has(vertex)) {
                    continue;
                }
                else {
                    matrices.add(vertex);
                }
            }
            for (const adjacentNode of this.graph.adjacentNodes(vertex)) {
                this.graph.markNodeAsSpecialRecentlyChanged(adjacentNode);
            }
            this.removeVertex(vertex);
            this.addressMapping.removeCell(adr);
        }
        this.stats.measure(StatType.ADJUSTING_MATRIX_MAPPING, () => {
            for (const matrix of matrices.values()) {
                this.matrixMapping.removeMatrix(matrix.getRange());
            }
        });
        this.stats.measure(StatType.ADJUSTING_RANGES, () => {
            const rangesToRemove = this.rangeMapping.removeRangesInSheet(removedSheetId);
            for (const range of rangesToRemove) {
                this.removeVertex(range);
            }
            this.stats.measure(StatType.ADJUSTING_ADDRESS_MAPPING, () => {
                this.addressMapping.removeSheet(removedSheetId);
            });
        });
        this.addStructuralNodesToChangeSet();
    }
    clearSheet(sheetId) {
        const matrices = new Set();
        for (const [address, vertex] of this.addressMapping.sheetEntries(sheetId)) {
            if (vertex instanceof MatrixVertex) {
                matrices.add(vertex);
            }
            else {
                this.setCellEmpty(address);
            }
        }
        for (const matrix of matrices.values()) {
            this.setMatrixEmpty(matrix);
        }
        this.addStructuralNodesToChangeSet();
    }
    removeColumns(removedColumns) {
        if (this.matrixMapping.isFormulaMatrixInColumns(removedColumns)) {
            throw Error('It is not possible to remove column within matrix');
        }
        this.stats.measure(StatType.ADJUSTING_GRAPH, () => {
            for (const vertex of this.addressMapping.verticesFromColumnsSpan(removedColumns)) {
                for (const adjacentNode of this.graph.adjacentNodes(vertex)) {
                    this.graph.markNodeAsSpecialRecentlyChanged(adjacentNode);
                }
                if (vertex instanceof MatrixVertex) {
                    continue;
                }
                this.removeVertex(vertex);
            }
        });
        this.stats.measure(StatType.ADJUSTING_MATRIX_MAPPING, () => {
            this.truncateMatricesAfterRemovingColumns(removedColumns);
        });
        this.stats.measure(StatType.ADJUSTING_ADDRESS_MAPPING, () => {
            this.addressMapping.removeColumns(removedColumns);
        });
        this.stats.measure(StatType.ADJUSTING_RANGES, () => {
            this.truncateRanges(removedColumns, address => address.col);
        });
        this.addStructuralNodesToChangeSet();
    }
    addRows(addedRows) {
        this.stats.measure(StatType.ADJUSTING_ADDRESS_MAPPING, () => {
            this.addressMapping.addRows(addedRows.sheet, addedRows.rowStart, addedRows.numberOfRows);
        });
        this.stats.measure(StatType.ADJUSTING_MATRIX_MAPPING, () => {
            this.expandMatricesAfterAddingRows(addedRows.sheet, addedRows.rowStart, addedRows.numberOfRows);
        });
        this.stats.measure(StatType.ADJUSTING_RANGES, () => {
            this.rangeMapping.moveAllRangesInSheetAfterRowByRows(addedRows.sheet, addedRows.rowStart, addedRows.numberOfRows);
            this.fixRangesWhenAddingRows(addedRows.sheet, addedRows.rowStart, addedRows.numberOfRows);
        });
        for (const vertex of this.addressMapping.verticesFromRowsSpan(addedRows)) {
            this.graph.markNodeAsSpecialRecentlyChanged(vertex);
        }
        this.addStructuralNodesToChangeSet();
    }
    addColumns(addedColumns) {
        this.stats.measure(StatType.ADJUSTING_ADDRESS_MAPPING, () => {
            this.addressMapping.addColumns(addedColumns.sheet, addedColumns.columnStart, addedColumns.numberOfColumns);
        });
        this.stats.measure(StatType.ADJUSTING_MATRIX_MAPPING, () => {
            this.expandMatricesAfterAddingColumns(addedColumns.sheet, addedColumns.columnStart, addedColumns.numberOfColumns);
        });
        this.stats.measure(StatType.ADJUSTING_RANGES, () => {
            this.rangeMapping.moveAllRangesInSheetAfterColumnByColumns(addedColumns.sheet, addedColumns.columnStart, addedColumns.numberOfColumns);
            this.fixRangesWhenAddingColumns(addedColumns.sheet, addedColumns.columnStart, addedColumns.numberOfColumns);
        });
        for (const vertex of this.addressMapping.verticesFromColumnsSpan(addedColumns)) {
            this.graph.markNodeAsSpecialRecentlyChanged(vertex);
        }
        this.addStructuralNodesToChangeSet();
    }
    ensureNoMatrixInRange(range) {
        if (this.matrixMapping.isFormulaMatrixInRange(range)) {
            throw Error('It is not possible to move / replace cells with matrix');
        }
    }
    moveCells(sourceRange, toRight, toBottom, toSheet) {
        for (const sourceAddress of sourceRange.addressesWithDirection(toRight, toBottom, this)) {
            const targetAddress = simpleCellAddress(toSheet, sourceAddress.col + toRight, sourceAddress.row + toBottom);
            let sourceVertex = this.addressMapping.getCell(sourceAddress);
            const targetVertex = this.addressMapping.getCell(targetAddress);
            this.addressMapping.removeCell(sourceAddress);
            if (sourceVertex !== null) {
                this.graph.markNodeAsSpecialRecentlyChanged(sourceVertex);
                this.addressMapping.setCell(targetAddress, sourceVertex);
                let emptyVertex = null;
                for (const adjacentNode of this.graph.adjacentNodes(sourceVertex)) {
                    if (adjacentNode instanceof RangeVertex && !sourceRange.containsRange(adjacentNode.range)) {
                        emptyVertex = emptyVertex || this.fetchCellOrCreateEmpty(sourceAddress);
                        this.graph.addEdge(emptyVertex, adjacentNode);
                        this.graph.removeEdge(sourceVertex, adjacentNode);
                    }
                }
                if (emptyVertex) {
                    this.graph.markNodeAsSpecialRecentlyChanged(emptyVertex);
                    this.addressMapping.setCell(sourceAddress, emptyVertex);
                }
            }
            if (targetVertex !== null) {
                if (sourceVertex === null) {
                    this.addressMapping.removeCell(targetAddress);
                }
                for (const adjacentNode of this.graph.adjacentNodes(targetVertex)) {
                    sourceVertex = sourceVertex !== null && sourceVertex !== void 0 ? sourceVertex : this.fetchCellOrCreateEmpty(targetAddress);
                    this.graph.addEdge(sourceVertex, adjacentNode);
                    this.graph.markNodeAsSpecialRecentlyChanged(sourceVertex);
                }
                this.removeVertex(targetVertex);
            }
        }
        for (const rangeVertex of this.rangeMapping.rangeVerticesContainedInRange(sourceRange)) {
            for (const adjacentNode of this.graph.adjacentNodes(rangeVertex)) {
                if (adjacentNode instanceof RangeVertex && !sourceRange.containsRange(adjacentNode.range)) {
                    this.graph.removeEdge(rangeVertex, adjacentNode);
                    for (const address of rangeVertex.range.addresses(this)) {
                        const newEmptyVertex = this.fetchCellOrCreateEmpty(address);
                        this.graph.addEdge(newEmptyVertex, adjacentNode);
                        this.addressMapping.setCell(address, newEmptyVertex);
                        this.graph.markNodeAsSpecialRecentlyChanged(newEmptyVertex);
                    }
                }
            }
        }
        this.rangeMapping.moveRangesInsideSourceRange(sourceRange, toRight, toBottom, toSheet);
    }
    disableNumericMatrices() {
        for (const [_, matrixVertex] of this.matrixMapping.numericMatrices()) {
            this.breakNumericMatrix(matrixVertex);
        }
    }
    breakNumericMatricesInRange(range) {
        for (const [_, matrix] of this.matrixMapping.numericMatricesInRange(range)) {
            this.breakNumericMatrix(matrix);
        }
    }
    breakNumericMatrix(matrixVertex) {
        const matrixRange = AbsoluteCellRange.spanFrom(matrixVertex.getAddress(), matrixVertex.width, matrixVertex.height);
        const adjacentNodes = this.graph.adjacentNodes(matrixVertex);
        for (const address of matrixRange.addresses(this)) {
            // We wouldn't need that typecast if we would take values from Matrix
            const valueVertex = new ValueCellVertex(this.getCellValue(address), this.getRawValue(address));
            this.addVertex(address, valueVertex);
        }
        for (const adjacentNode of adjacentNodes.values()) {
            const nodeDependencies = collectAddressesDependentToMatrix(this.functionRegistry, adjacentNode, matrixVertex, this.lazilyTransformingAstService, this);
            for (const address of nodeDependencies) {
                const vertex = this.fetchCell(address);
                this.graph.addEdge(vertex, adjacentNode);
            }
        }
        this.removeVertex(matrixVertex);
        this.matrixMapping.removeMatrix(matrixVertex.getRange());
    }
    setMatrixEmpty(matrixVertex) {
        const matrixRange = AbsoluteCellRange.spanFrom(matrixVertex.getAddress(), matrixVertex.width, matrixVertex.height);
        const adjacentNodes = this.graph.adjacentNodes(matrixVertex);
        for (const address of matrixRange.addresses(this)) {
            this.addressMapping.removeCell(address);
        }
        for (const adjacentNode of adjacentNodes.values()) {
            const nodeDependencies = collectAddressesDependentToMatrix(this.functionRegistry, adjacentNode, matrixVertex, this.lazilyTransformingAstService, this);
            for (const address of nodeDependencies) {
                const vertex = this.fetchCellOrCreateEmpty(address);
                this.graph.addEdge(vertex, adjacentNode);
            }
            if (nodeDependencies.length > 0) {
                this.graph.markNodeAsSpecialRecentlyChanged(adjacentNode);
            }
        }
        this.removeVertex(matrixVertex);
        this.matrixMapping.removeMatrix(matrixVertex.getRange());
    }
    addVertex(address, vertex) {
        this.graph.addNode(vertex);
        this.setVertexAddress(address, vertex);
    }
    addMatrixVertex(address, vertex) {
        this.graph.addNode(vertex);
        this.setAddressMappingForMatrixVertex(vertex, address);
    }
    addNewMatrixVertex(matrixVertex) {
        const range = AbsoluteCellRange.spanFrom(matrixVertex.getAddress(), matrixVertex.width, matrixVertex.height);
        for (const vertex of this.verticesFromRange(range)) {
            if (vertex instanceof MatrixVertex) {
                throw Error('You cannot modify only part of an array');
            }
        }
        this.setMatrix(range, matrixVertex);
        for (const [address, vertex] of this.entriesFromRange(range)) {
            if (vertex) {
                this.exchangeGraphNode(vertex, matrixVertex);
            }
            this.setVertexAddress(address, matrixVertex);
        }
    }
    *matrixFormulaNodes() {
        for (const vertex of this.graph.nodes) {
            if (vertex instanceof MatrixVertex && vertex.isFormula()) {
                yield vertex;
            }
        }
    }
    *entriesFromRowsSpan(rowsSpan) {
        yield* this.addressMapping.entriesFromRowsSpan(rowsSpan);
    }
    *entriesFromColumnsSpan(columnsSpan) {
        yield* this.addressMapping.entriesFromColumnsSpan(columnsSpan);
    }
    existsVertex(address) {
        return this.addressMapping.has(address);
    }
    fetchCell(address) {
        return this.addressMapping.fetchCell(address);
    }
    getCell(address) {
        return this.addressMapping.getCell(address);
    }
    getCellValue(address) {
        return this.addressMapping.getCellValue(address);
    }
    getRawValue(address) {
        return this.addressMapping.getRawValue(address);
    }
    getScalarValue(address) {
        const value = this.addressMapping.getCellValue(address);
        if (value instanceof SimpleRangeValue) {
            return new CellError(ErrorType.VALUE, ErrorMessage.ScalarExpected);
        }
        return value;
    }
    setVertexAddress(address, vertex) {
        this.addressMapping.setCell(address, vertex);
    }
    existsEdge(fromNode, toNode) {
        return this.graph.existsEdge(fromNode, toNode);
    }
    getSheetId(sheetName) {
        return this.sheetMapping.fetch(sheetName);
    }
    getSheetName(sheetId) {
        return this.sheetMapping.fetchDisplayName(sheetId);
    }
    getSheetHeight(sheet) {
        return this.addressMapping.getHeight(sheet);
    }
    getSheetWidth(sheet) {
        return this.addressMapping.getWidth(sheet);
    }
    getMatrix(range) {
        return this.matrixMapping.getMatrix(range);
    }
    setMatrix(range, vertex) {
        this.matrixMapping.setMatrix(range, vertex);
    }
    getRange(start, end) {
        return this.rangeMapping.getRange(start, end);
    }
    topSortWithScc() {
        return this.graph.topSortWithScc();
    }
    markAsVolatile(vertex) {
        this.graph.markNodeAsSpecial(vertex);
    }
    markAsDependentOnStructureChange(vertex) {
        this.graph.markNodeAsChangingWithStructure(vertex);
    }
    forceApplyPostponedTransformations() {
        for (const vertex of this.graph.nodes.values()) {
            if (vertex instanceof FormulaCellVertex) {
                vertex.ensureRecentData(this.lazilyTransformingAstService);
            }
        }
    }
    volatileVertices() {
        return this.graph.specialNodes;
    }
    destroy() {
        this.graph.destroy();
        this.addressMapping.destroy();
        this.rangeMapping.destroy();
        this.sheetMapping.destroy();
        this.matrixMapping.destroy();
    }
    *verticesFromRange(range) {
        for (const address of range.addresses(this)) {
            const vertex = this.getCell(address);
            if (vertex) {
                yield vertex;
            }
        }
    }
    *rawValuesFromRange(range) {
        for (const address of range.addresses(this)) {
            const value = this.getScalarValue(address);
            if (value !== EmptyValue) {
                yield [getRawValue(value), address];
            }
        }
    }
    *entriesFromRange(range) {
        for (const address of range.addresses(this)) {
            yield [address, this.getCell(address)];
        }
    }
    exchangeGraphNode(oldNode, newNode) {
        this.graph.addNode(newNode);
        const adjNodesStored = this.graph.adjacentNodes(oldNode);
        this.removeVertex(oldNode);
        adjNodesStored.forEach((adjacentNode) => {
            if (this.graph.hasNode(adjacentNode)) {
                this.graph.addEdge(newNode, adjacentNode);
            }
        });
    }
    exchangeOrAddGraphNode(oldNode, newNode) {
        if (oldNode) {
            this.exchangeGraphNode(oldNode, newNode);
        }
        else {
            this.graph.addNode(newNode);
        }
    }
    computeListOfValuesInRange(range) {
        const values = [];
        for (const cellFromRange of range.addresses(this)) {
            const value = this.getScalarValue(cellFromRange);
            values.push(value);
        }
        return values;
    }
    addStructuralNodesToChangeSet() {
        for (const vertex of this.graph.specialNodesStructuralChanges) {
            this.graph.markNodeAsSpecialRecentlyChanged(vertex);
        }
    }
    fixRangesWhenAddingRows(sheet, row, numberOfRows) {
        const originalValues = Array.from(this.rangeMapping.rangesInSheet(sheet));
        for (const rangeVertex of originalValues) {
            if (rangeVertex.range.includesRow(row + numberOfRows)) {
                if (rangeVertex.bruteForce) {
                    const addedSubrangeInThatRange = rangeVertex.range.rangeWithSameWidth(row, numberOfRows);
                    for (const address of addedSubrangeInThatRange.addresses(this)) {
                        this.graph.addEdge(this.fetchCellOrCreateEmpty(address), rangeVertex);
                    }
                }
                else {
                    let currentRangeVertex = rangeVertex;
                    let find = this.rangeMapping.findSmallerRange(currentRangeVertex.range);
                    if (find.smallerRangeVertex !== null) {
                        continue;
                    }
                    while (find.smallerRangeVertex === null) {
                        const newRangeVertex = new RangeVertex(AbsoluteCellRange.spanFrom(currentRangeVertex.range.start, currentRangeVertex.range.width(), currentRangeVertex.range.height() - 1));
                        this.rangeMapping.setRange(newRangeVertex);
                        this.graph.addNode(newRangeVertex);
                        const restRange = new AbsoluteCellRange(simpleCellAddress(currentRangeVertex.range.start.sheet, currentRangeVertex.range.start.col, currentRangeVertex.range.end.row), currentRangeVertex.range.end);
                        this.addAllFromRange(restRange, currentRangeVertex);
                        this.graph.addEdge(newRangeVertex, currentRangeVertex);
                        currentRangeVertex = newRangeVertex;
                        find = this.rangeMapping.findSmallerRange(currentRangeVertex.range);
                    }
                    this.graph.addEdge(find.smallerRangeVertex, currentRangeVertex);
                    this.addAllFromRange(find.restRange, currentRangeVertex);
                    this.graph.removeEdge(find.smallerRangeVertex, rangeVertex);
                }
            }
        }
    }
    addAllFromRange(range, vertex) {
        for (const address of range.addresses(this)) {
            this.graph.addEdge(this.fetchCellOrCreateEmpty(address), vertex);
        }
    }
    fixRangesWhenAddingColumns(sheet, column, numberOfColumns) {
        for (const rangeVertex of this.rangeMapping.rangesInSheet(sheet)) {
            if (rangeVertex.range.includesColumn(column + numberOfColumns)) {
                let subrange;
                if (rangeVertex.bruteForce) {
                    subrange = rangeVertex.range.rangeWithSameHeight(column, numberOfColumns);
                }
                else {
                    subrange = AbsoluteCellRange.spanFrom(simpleCellAddress(sheet, column, rangeVertex.range.end.row), numberOfColumns, 1);
                }
                for (const address of subrange.addresses(this)) {
                    this.graph.addEdge(this.fetchCellOrCreateEmpty(address), rangeVertex);
                }
            }
        }
    }
    setAddressMappingForMatrixVertex(vertex, formulaAddress) {
        this.setVertexAddress(formulaAddress, vertex);
        if (!(vertex instanceof MatrixVertex)) {
            return;
        }
        const range = AbsoluteCellRange.spanFrom(formulaAddress, vertex.width, vertex.height);
        this.setMatrix(range, vertex);
        for (const address of range.addresses(this)) {
            this.setVertexAddress(address, vertex);
        }
    }
    truncateMatricesAfterRemovingRows(removedRows) {
        const verticesToRemove = this.matrixMapping.truncateMatricesByRows(removedRows);
        verticesToRemove.forEach((vertex) => {
            this.removeVertex(vertex);
        });
    }
    truncateRanges(span, coordinate) {
        const { verticesToRemove, verticesToMerge } = this.rangeMapping.truncateRanges(span, coordinate);
        for (const [existingVertex, mergedVertex] of verticesToMerge) {
            this.mergeRangeVertices(existingVertex, mergedVertex);
        }
        for (const rangeVertex of verticesToRemove) {
            this.removeVertexAndCleanupDependencies(rangeVertex);
        }
    }
    truncateMatricesAfterRemovingColumns(removedColumns) {
        const verticesToRemove = this.matrixMapping.truncateMatricesByColumns(removedColumns);
        verticesToRemove.forEach((vertex) => {
            this.removeVertex(vertex);
        });
    }
    expandMatricesAfterAddingRows(sheet, rowStart, numberOfRows) {
        for (const [, matrix] of this.matrixMapping.numericMatricesInRows(RowsSpan.fromRowStartAndEnd(sheet, rowStart, rowStart))) {
            matrix.addRows(sheet, rowStart, numberOfRows);
            const addedRange = AbsoluteCellRange.spanFrom(simpleCellAddress(sheet, matrix.getAddress().col, rowStart), matrix.width, numberOfRows);
            for (const address of addedRange.addresses(this)) {
                this.addressMapping.setCell(address, matrix);
            }
        }
    }
    expandMatricesAfterAddingColumns(sheet, columnStart, numberOfColumns) {
        for (const [, matrix] of this.matrixMapping.numericMatricesInColumns(ColumnsSpan.fromColumnStartAndEnd(sheet, columnStart, columnStart))) {
            matrix.addColumns(sheet, columnStart, numberOfColumns);
            const addedRange = AbsoluteCellRange.spanFrom(simpleCellAddress(sheet, columnStart, matrix.getAddress().row), numberOfColumns, matrix.height);
            for (const address of addedRange.addresses(this)) {
                this.addressMapping.setCell(address, matrix);
            }
        }
    }
    removeVertex(vertex) {
        this.removeVertexAndCleanupDependencies(vertex);
        if (vertex instanceof RangeVertex) {
            this.rangeMapping.removeRange(vertex);
        }
    }
    mergeRangeVertices(existingVertex, newVertex) {
        const adjNodesStored = this.graph.adjacentNodes(newVertex);
        this.removeVertexAndCleanupDependencies(newVertex);
        this.graph.softRemoveEdge(existingVertex, newVertex);
        adjNodesStored.forEach((adjacentNode) => {
            if (this.graph.hasNode(adjacentNode)) {
                this.graph.addEdge(existingVertex, adjacentNode);
            }
        });
    }
    removeVertexAndCleanupDependencies(inputVertex) {
        const dependencies = new Set(this.graph.removeNode(inputVertex));
        while (dependencies.size > 0) {
            const vertex = dependencies.values().next().value;
            dependencies.delete(vertex);
            if (this.graph.hasNode(vertex) && this.graph.adjacentNodesCount(vertex) === 0) {
                if (vertex instanceof RangeVertex || vertex instanceof EmptyCellVertex) {
                    this.graph.removeNode(vertex).forEach((candidate) => dependencies.add(candidate));
                }
                if (vertex instanceof RangeVertex) {
                    this.rangeMapping.removeRange(vertex);
                }
                else if (vertex instanceof EmptyCellVertex) {
                    this.addressMapping.removeCell(vertex.address);
                }
            }
        }
    }
    getAdjacentNodesAddresses(inputVertex) {
        const deps = this.graph.adjacentNodes(inputVertex);
        const ret = [];
        deps.forEach((vertex) => {
            const castVertex = vertex;
            if (castVertex instanceof RangeVertex) {
                ret.push(new AbsoluteCellRange(castVertex.start, castVertex.end));
            }
            else if (castVertex instanceof FormulaCellVertex) {
                ret.push(castVertex.getAddress(this.lazilyTransformingAstService));
            }
            else {
                ret.push(castVertex.getAddress());
            }
        });
        return ret;
    }
}
